import { useState } from "react";
import * as CollapsiblePrimitive from "@radix-ui/react-collapsible";
import { ChevronRight, Menu, X } from "lucide-react";
import type { NavItem } from "@/app/content";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from "@/app/components/ui/sheet";
import { cn } from "@/app/components/ui/utils";

type MobileNavDrawerProps = {
  navItems: NavItem[];
  menuLabel?: string;
  closeLabel?: string;
  navTitle?: string;
};

export function MobileNavDrawer({
  navItems,
  menuLabel = "打开菜单",
  closeLabel = "关闭菜单",
  navTitle = "菜单",
}: MobileNavDrawerProps) {
  const [open, setOpen] = useState(false);
  const [githubOpen, setGithubOpen] = useState(false);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          className="inline-flex h-12 w-12 flex-shrink-0 items-center justify-center border-4 border-black bg-white/75 backdrop-blur-sm shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transition-transform hover:-translate-x-0.5 hover:-translate-y-0.5 hover:bg-white/90 active:translate-x-[4px] active:translate-y-[4px] active:shadow-none"
          aria-label={menuLabel}
        >
          <Menu className="h-6 w-6" strokeWidth={3} />
        </button>
      </SheetTrigger>

      <SheetContent side="left" className="p-0" aria-describedby={undefined}>
        <div className="flex h-full flex-col">
          <div className="flex items-center justify-between border-b-4 border-black px-4 py-4">
            <SheetTitle className="text-sm tracking-[0.2em]">{navTitle}</SheetTitle>
            <SheetClose asChild>
              <button
                type="button"
                className="inline-flex h-10 w-10 items-center justify-center border-4 border-black bg-white shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] transition-transform hover:-translate-x-0.5 hover:-translate-y-0.5 active:translate-x-[3px] active:translate-y-[3px] active:shadow-none"
                aria-label={closeLabel}
              >
                <X className="h-5 w-5" strokeWidth={3} />
              </button>
            </SheetClose>
          </div>

          <SheetDescription className="sr-only">
            Site navigation
          </SheetDescription>

          <nav className="flex-1 overflow-y-auto px-4 py-4">
            <ul className="space-y-2">
              {navItems.map((item) => (
                <li key={item.href ?? item.label}>
                  {item.children ? (
                    <GitHubNavCollapsible
                      item={item}
                      open={githubOpen}
                      onOpenChange={setGithubOpen}
                      onNavigate={() => setOpen(false)}
                    />
                  ) : (
                    <SheetClose asChild>
                      <a
                        href={item.href}
                        className="block border-4 border-black bg-white/70 px-4 py-3 text-sm font-black uppercase text-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] backdrop-blur-sm transition-colors hover:bg-[#A3E635]/30"
                      >
                        {item.label}
                      </a>
                    </SheetClose>
                  )}
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function GitHubNavCollapsible({
  item,
  open,
  onOpenChange,
  onNavigate,
}: {
  item: NavItem;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onNavigate: () => void;
}) {
  if (!item.children) {
    return null;
  }

  return (
    <CollapsiblePrimitive.Root open={open} onOpenChange={onOpenChange}>
      <CollapsiblePrimitive.Trigger className="flex w-full items-center justify-between border-4 border-black bg-white/70 px-4 py-3 text-left text-sm font-black uppercase text-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] backdrop-blur-sm transition-colors hover:bg-[#A3E635]/20">
        <span>{item.label}</span>
        <ChevronRight
          className={cn("h-4 w-4 transition-transform", open && "rotate-90")}
          strokeWidth={3}
        />
      </CollapsiblePrimitive.Trigger>
      <CollapsiblePrimitive.Content className="mt-2 space-y-2 border-l-4 border-black/20 pl-3">
        {item.children.map((child) => {
          const isExternal = child.href.startsWith("http");
          return (
            <SheetClose asChild key={child.href}>
              <a
                href={child.href}
                {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                onClick={onNavigate}
                className="block border-4 border-black bg-white/60 px-3 py-3 backdrop-blur-sm transition-colors hover:bg-[#A3E635]/20"
              >
                <span className="text-sm font-black text-black">{child.label}</span>
                {child.description ? (
                  <span className="mt-0.5 block text-xs font-bold text-zinc-500">{child.description}</span>
                ) : null}
              </a>
            </SheetClose>
          );
        })}
      </CollapsiblePrimitive.Content>
    </CollapsiblePrimitive.Root>
  );
}
